/**
 * Rótulos dos campos do contrato de extração (ADR-025), como o protótipo os mostra
 * (`SECTIONS_TERMO`/`SECTIONS_DECL`). Usados no painel "Regras aplicadas" da ficha.
 * ADR-048: os 10 campos do equipamento (o grupo "Equipamento" da ficha) e os campos lidos nas evidências
 * complementares ("Outras evidências").
 */

/**
 * ADR-048 ([OPEN-1], design §1.2 e §10): os campos do equipamento satelital do termo, na ordem do grupo "Equipamento"
 * da ficha (e das colunas da exportação).
 */
export const CAMPOS_EQUIPAMENTO: readonly string[] = [
  'os_designacao',
  'modem_serie',
  'modem_mac',
  'radio_serie',
  'antena_serie',
  'antena_tamanho',
  'satelite',
  'terminal_id',
  'ip_lan',
  'sinal',
];

export const ROTULO_CAMPO: Readonly<Record<string, string>> = {
  razao_social: 'Razão Social (escola)',
  codigo_inep: 'Código da Escola (INEP)',
  cnpj: 'CNPJ',
  nome_gestor: 'Nome do Gestor',
  cpf_gestor: 'CPF do Gestor',
  telefone_celular: 'Telefone celular',
  email: 'E-mail',
  endereco: 'Endereço (nome e compl.)',
  bairro: 'Bairro',
  municipio: 'Município',
  uf: 'UF',
  cep: 'CEP',
  imei: 'IMEI do aparelho',
  iccid: 'ICCID do chip',
  latitude: 'Latitude',
  longitude: 'Longitude',
  erb: 'ERB conectada / próxima',
  instalacao_sucesso: 'Instalação com sucesso?',
  causa_produtiva: 'Causa produtiva',
  causa_improdutiva: 'Causa improdutiva',
  motivo_insucesso: 'Motivo do insucesso',
  vel_contratada_down: 'Veloc. contratada (down)',
  vel_contratada_up: 'Veloc. contratada (up)',
  vel_medida_down: 'Veloc. medida download',
  vel_medida_up: 'Veloc. medida upload',
  cobertura: 'Cobertura (3G/4G)',
  nome_representante: 'Representante legal',
  assinatura_responsavel: 'Assinatura do responsável',
  carimbo: 'Carimbo',
  carimbo_texto: 'Texto do carimbo',
  rg_representante: 'RG do representante',
  data: 'Data de instalação',
  local: 'Local',
  nome_tecnico: 'Técnico de campo',
  rg_tecnico: 'RG do técnico',
  assinatura_tecnico: 'Assinatura do técnico',
  contratada: 'Contratada',
  // ADR-048: o grupo "Equipamento" (design §10).
  os_designacao: 'OS / designação',
  modem_serie: 'Série do modem',
  modem_mac: 'MAC do modem',
  radio_serie: 'Série do rádio',
  antena_serie: 'Série da antena',
  antena_tamanho: 'Tamanho da antena',
  satelite: 'Satélite',
  terminal_id: 'Terminal',
  ip_lan: 'IP LAN',
  sinal: 'Sinal',
};

/** Rótulo do campo; o próprio nome quando não há rótulo (campo fora do contrato). */
export function rotuloCampo(nome: string): string {
  return ROTULO_CAMPO[nome] ?? nome;
}

/**
 * ADR-048 (design §1.3): os campos lidos em cada classe de evidência (`complemento`), no carimbo e no EXIF — quando a
 * chave não é um campo do termo ou tem, na evidência, outro sentido (o IMEI da etiqueta não é "do aparelho").
 */
const ROTULO_CAMPO_EVIDENCIA: Readonly<Record<string, string>> = {
  download: 'Download',
  upload: 'Upload',
  ping: 'Ping / latência',
  jitter: 'Jitter',
  perda: 'Perda de pacotes',
  data_hora: 'Data e hora',
  aplicativo: 'Aplicativo',
  download_bytes: 'Download (bytes)',
  download_segundos: 'Download (segundos)',
  download_kbps_s: 'Download (KB/s)',
  upload_bytes: 'Upload (bytes)',
  upload_segundos: 'Upload (segundos)',
  upload_kbps_s: 'Upload (KB/s)',
  nome_escola: 'Escola',
  operadora: 'Operadora',
  tecnologia: 'Tecnologia',
  cell_id: 'Cell ID',
  lac_tac: 'LAC / TAC',
  imei: 'IMEI',
  iccid: 'ICCID',
  fabricante: 'Fabricante',
  modelo: 'Modelo',
  numero_serie: 'Número de série',
  mac: 'MAC',
  escolas: 'Escolas citadas',
  orgao: 'Órgão',
  data: 'Data',
  tema: 'Tema',
  tecnico: 'Técnico',
  ordem_servico: 'Ordem de serviço',
  designacao: 'Designação',
  camera: 'Câmera',
  fuso: 'Fuso horário',
};

/**
 * Rótulo de um campo lido numa evidência complementar: o da evidência, senão o do termo, senão a chave legível
 * ("campo_novo" → "Campo novo") — nunca a chave crua na tela.
 */
export function rotuloCampoEvidencia(nome: string): string {
  const rotulo = ROTULO_CAMPO_EVIDENCIA[nome] ?? ROTULO_CAMPO[nome];
  if (rotulo) return rotulo;
  const legivel = nome.replace(/[._]+/g, ' ').trim();
  return legivel.charAt(0).toUpperCase() + legivel.slice(1);
}
